/**
 * Language Store
 * 
 * Manages application-wide language state using Zustand
 * Replaces the previous LanguageContext for consistency with other stores
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/utils/constants';
import { Language, BilingualText } from '@/types/common';
import { LanguageStore, TranslateFn } from './types';

/**
 * Type guard to check if language is valid
 */
const isValidLanguage = (lang: string | null): lang is Language => {
  return lang === 'en' || lang === 'es';
};

/**
 * Language store with persistence
 */
export const useLanguageStore = create<LanguageStore>()(
  persist(
    (set, get) => ({
      // Initial state
      language: 'en',

      // Actions
      setLanguage: (lang: Language) => set({ language: lang }),

      /**
       * Translate bilingual content to current language
       * Supports both (en, es) string args and BilingualText object
       */
      t: ((
        enTextOrBilingual: string | BilingualText,
        esText?: string
      ): string => {
        const { language } = get();
        
        // BilingualText object overload
        if (typeof enTextOrBilingual === 'object') {
          return language === 'en' ? enTextOrBilingual.en : enTextOrBilingual.es;
        }
        // String arguments overload
        return language === 'en' ? enTextOrBilingual : (esText ?? enTextOrBilingual);
      }) as TranslateFn,

      /**
       * Get text for a specific language from bilingual content
       */
      getText: (text: BilingualText, lang?: Language): string => {
        const targetLang = lang ?? get().language;
        return targetLang === 'en' ? text.en : text.es;
      },
    }),
    {
      name: STORAGE_KEYS.language,
      partialize: (state) => ({ language: state.language }),
      // Handle migration from old localStorage format
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Check if there's an old-format value in localStorage
          const oldValue = localStorage.getItem(STORAGE_KEYS.language);
          if (oldValue && isValidLanguage(oldValue)) {
            state.language = oldValue;
          }
        }
      },
    }
  )
);

/**
 * Convenience hook for just the language value
 */
export const useCurrentLanguage = () => useLanguageStore((state) => state.language);

/**
 * Convenience hook for the translation function
 */
export const useTranslate = () => useLanguageStore((state) => state.t);

/**
 * Convenience hook for language actions
 */
export const useLanguageActions = () => 
  useLanguageStore((state) => ({
    setLanguage: state.setLanguage,
    t: state.t,
    getText: state.getText,
  }));
